// src/contexts/useReloadOnUnrecoveredSubscription.ts
import { useEffect } from "react";
import { logToSentry } from "utils/logger";

const RELOAD_GRACE_MS = 15_000;
const RELOAD_STORM_WINDOW_MS = 5 * 60_000;
const STORAGE_KEY = "zombieReloadAt";

type Params = {
	isSubscriptionBroken: boolean;
	appConnected: boolean;
	fridgeId: string;
};

/**
 * Last-resort recovery for a subscription that won't revive. When the stream stays broken WHILE the
 * network is up (the zombie signature) past RELOAD_GRACE_MS — a live frame would have cleared
 * isSubscriptionBroken — reload the page (the automated version of the manual fix). Genuine offline
 * (appConnected false) is excluded; useNavigateHomeOnSustainedDisconnect owns that.
 *
 * A sessionStorage timestamp RATE-LIMITS reloads to one per RELOAD_STORM_WINDOW_MS so a
 * boot-into-bad-state can't reload-loop every 15s — but it must never LOCK OUT: when a reload lands
 * back in the zombie, we DEFER the next attempt to when the window elapses rather than giving up. (The
 * old code returned without rescheduling, and since the effect deps are constant in a steady zombie it
 * never re-armed — stranding the kiosk on the error modal until a manual remote reload.)
 */
export const useReloadOnUnrecoveredSubscription = ({ isSubscriptionBroken, appConnected, fridgeId }: Params): void => {
	useEffect(() => {
		if (!(isSubscriptionBroken && appConnected)) {
			return;
		}
		// `last === 0` means no prior auto-reload recorded — reload after the normal grace. Otherwise defer
		// the next reload until the window elapses (compared explicitly so a stored 0 can't skew it). A
		// steady zombie re-runs this effect on each reload (fresh page), so it keeps retrying ~once per
		// window until a live frame clears isSubscriptionBroken — at which point cleanup cancels the timer.
		const last = Number(sessionStorage.getItem(STORAGE_KEY) ?? "0");
		const stormWait = last === 0 ? 0 : Math.max(0, RELOAD_STORM_WINDOW_MS - (Date.now() - last));
		if (stormWait > 0) {
			logToSentry(
				`Subscription still unrecovered after a recent reload; deferring reload, fridge: ${fridgeId}`,
				"warning",
			);
		}
		const timer = window.setTimeout(() => {
			sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
			logToSentry(`Reloading to recover an unrecovered subscription, fridge: ${fridgeId}`, "warning");
			window.location.reload();
		}, RELOAD_GRACE_MS + stormWait);
		return () => window.clearTimeout(timer);
	}, [isSubscriptionBroken, appConnected, fridgeId]);
};
